import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import type { LayoutDocument } from '../lib/form-layout/types';
import type { ObligationFormFieldName } from '../lib/form-layout/obligation-fields';

export type ObligationLayoutDocument = LayoutDocument<ObligationFormFieldName>;

export interface ResolvedLayout {
  layout: ObligationLayoutDocument;
  scope: 'global' | 'project' | 'default';
  resolved_from: 'project' | 'global' | 'none';
  source_id: string | null;
  updated_at: string | null;
  notes: string | null;
}

export const obligationFormLayoutKeys = {
  all: ['obligation-form-layout'] as const,
  active: (projectId: string | null) =>
    [...obligationFormLayoutKeys.all, 'active', projectId] as const,
  adminActive: (scope: 'global' | 'project', projectId: string | null) =>
    [...obligationFormLayoutKeys.all, 'admin', 'active', scope, projectId] as const,
};

export function useObligationFormLayout(projectId: string | null) {
  return useQuery({
    queryKey: obligationFormLayoutKeys.active(projectId),
    staleTime: 60_000,
    queryFn: () =>
      api.get<ResolvedLayout>(
        '/obligation-form-layout',
        projectId ? { project_id: projectId } : undefined,
      ),
  });
}

export function useAdminActiveObligationLayout(
  scope: 'global' | 'project',
  projectId: string | null,
) {
  return useQuery({
    queryKey: obligationFormLayoutKeys.adminActive(scope, projectId),
    queryFn: () => {
      const q: Record<string, string> = { scope };
      if (scope === 'project' && projectId) q.project_id = projectId;
      return api.get<ResolvedLayout>('/admin/obligation-form-layout/active', q);
    },
    enabled: scope === 'global' || projectId !== null,
  });
}

export interface UpdateObligationLayoutInput {
  scope: 'global' | 'project';
  project_id?: string | null;
  layout: ObligationLayoutDocument;
  notes?: string | null;
}

export function useUpdateObligationFormLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateObligationLayoutInput) =>
      api.put<ResolvedLayout>('/admin/obligation-form-layout', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: obligationFormLayoutKeys.all }),
  });
}
